<script>
    import { PRConnect } from "@projectroom/ui-lib";
    import axios from "axios";
    import { onDestroy, onMount } from "svelte";
    import { Section, Switch } from "../lib/ui";
    import ImageUploadCard from "./chat-builder/ImageUploadCard.svelte";


    export let bot;

    let lang = "ko";

    // 버튼 CSS 그룹(동작/섹션/콘텐츠)의 색상 피커 ref.
    // 한 그룹의 5개 값을 한 번에 읽어 하나의 buttonCss 객체로 저장한다 → 개별 ref 유지 필요.
    let refActionButtonColor,
        refActionButtonLineColor,
        refActionButtonMouseOverColor,
        refActionButtonMouseOverLineColor,
        refActionButtonFontColor;
    let refConversationButtonColor,
        refConversationButtonLineColor,
        refConversationButtonMouseOverColor,
        refConversationButtonMouseOverLineColor,
        refConversationButtonFontColor;
    let refFixedConversationButtonColor,
        refFixedConversationButtonLineColor,
        refFixedConversationButtonMouseOverColor,
        refFixedConversationButtonMouseOverLineColor,
        refFixedConversationButtonFontColor;

    onMount(() => {});
    onDestroy(() => {});

    // ── 이미지 업로드 (배경 / 로고) ── ImageUploadCard 의 file 이벤트에서 { target:{ files } } 전달 ──
    const onBackgroundImageSelected = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const key = `${bot.id}/common/image/${file.name}`;
        const type = file.type ? file.type : "application/octet-stream";
        PRConnect.send("/api/File/uploadPresignedUrl", { key, type }, (data) => {
            axios
                .put(data.result, file, { headers: { "Content-Type": type } })
                .then((response) => {
                    if (response.status == 200) {
                        bot.changeBackImageContent(file.name);
                    }
                });
        });
    };

    const onLogoImageSelected = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const key = `${bot.defaultChar.botId}/common/logo-image/${file.name}`;
        const type = file.type ? file.type : "application/octet-stream";
        PRConnect.send("/api/File/uploadPresignedUrl", { key, type }, (data) => {
            axios
                .put(data.result, file, { headers: { "Content-Type": type } })
                .then((response) => {
                    if (response.status == 200) {
                        bot.changeLogoImageContent(file.name);
                    }
                });
        });
    };

    // ── 색상 변경 (toolcool-color-picker on:mouseup) ──
    const changeBackColor = (e) => {
        const color = e.target.color.toRgbString();
        bot.changeBackColor(color);
    };

    const changeLineColor = (e) => {
        const color = e.target.color;
        bot.changeLineColor(color);
    };

    const changeActionButtonCss = () => {
        bot.changeActionButtonCss({
            color: refActionButtonColor.color.toRgbString(),
            lineColor: refActionButtonLineColor.color.toRgbString(),
            mouseOverColor: refActionButtonMouseOverColor.color.toRgbString(),
            mouseOverLineColor:
                refActionButtonMouseOverLineColor.color.toRgbString(),
            fontColor: refActionButtonFontColor.color.toRgbString(),
        });
    };

    const changeConversationButtonCss = () => {
        bot.changeConversationButtonCss({
            color: refConversationButtonColor.color.toRgbString(),
            lineColor: refConversationButtonLineColor.color.toRgbString(),
            mouseOverColor:
                refConversationButtonMouseOverColor.color.toRgbString(),
            mouseOverLineColor:
                refConversationButtonMouseOverLineColor.color.toRgbString(),
            fontColor: refConversationButtonFontColor.color.toRgbString(),
        });
    };

    const changeFixedConversationButtonCss = () => {
        bot.changeFixedConversationButtonCss({
            color: refFixedConversationButtonColor.color.toRgbString(),
            lineColor: refFixedConversationButtonLineColor.color.toRgbString(),
            mouseOverColor:
                refFixedConversationButtonMouseOverColor.color.toRgbString(),
            mouseOverLineColor:
                refFixedConversationButtonMouseOverLineColor.color.toRgbString(),
            fontColor: refFixedConversationButtonFontColor.color.toRgbString(),
        });
    };

    const changeConversationTextColor = (e) => {
        bot.changeConversationTextColor(e.target.color.toRgbString());
    };

    const changeConversationLineColor = (e) => {
        bot.changeConversationLineColor(e.target.color.toRgbString());
    };

    // ── 이미지 삭제 ──
    const clickBackImageDelete = () => bot.changeBackImageContent(undefined);
    const clickLogoImageDelete = () => bot.changeLogoImageContent(null);

    // ── 버튼 모양 / 외곽선 / 폰트 (권한 게이트 후 반영) ──
    const roundOptions = [
        { round: "8", label: "둥글게", radius: "10px" },
        { round: "20", label: "동글동글", radius: "22px" },
        { round: "0", label: "네모네모", radius: "3px" },
    ];
    const borderOptions = [
        { width: "0", label: "외각선 없음", border: "0px" },
        { width: "1", label: "얇은 선", border: "1.5px" },
        { width: "2", label: "두꺼운 선", border: "3px" },
    ];
    const fontList = [
        { key: "NotoSansKRBold", label: "노토산스 Bold", sample: "노토산스" },
        { key: "NanumSquareBold", label: "나눔 스퀘어 Bold", sample: "나눔 스퀘어" },
        { key: "NanumSquareRound", label: "나눔 라운드 Bold", sample: "나눔 라운드" },
        { key: "Katuri", label: "안동 엄마 까투리", sample: "안동엄마까투리" },
        { key: "Neodgm", label: "neo둥근모", sample: "neo둥근모" },
        { key: "SeoulHangangExtraBold", label: "서울 한강 Extra Bold", sample: "서울한강" },
        { key: "IMHyemin", label: "IM혜민체 Bold", sample: "IM혜민체" },
        { key: "UhBeeZZIBA", label: "어비찌빠체 Regular", sample: "어비찌빠체" },
    ];

    const selectRound = (round) => {
        if (!bot.isAuthority("availableType", `BUTTON_ROUND_${round}`)) return;
        bot.changeButtonRound(round);
    };
    const selectBorderWidth = (width) => {
        if (!bot.isAuthority("availableType", `BUTTON_BORDER_${width}`)) return;
        bot.changeButtonBorderWidth(width);
    };
    const selectFont = (fontType) => {
        if (!bot.isAuthority("fontType", fontType)) return;
        bot.changeConversationTextType(fontType);
    };

    // ── 토글 (권한 게이트) ──
    const clickChangeLogoView = () => {
        if (!bot.isAuthority("availableType", "AVATALK_LOGO")) return;
        bot.changeAvatalkImageFlag();
    };
    const clickChangeShareView = () => {
        if (!bot.isAuthority("availableType", "SHARE_BUTTON")) return;
        bot.changeShareButtonFlag();
    };
    const clickChangeBackBlurFlag = () => {
        if (!bot.isAuthority("availableType", "BACK_BLUR")) return;
        bot.changeBackBlurFlag();
    };
    const clickChangeBackDarklyFlag = () => {
        if (!bot.isAuthority("availableType", "BACK_DARKLY")) return;
        bot.changeBackDarklyFlag();
    };

    // ── 허브 테마 색 (허브 빌더 전용) ──
    // 고르면 **기존 디자인 값**을 한 번에 바꿔 저장한다: 배경 색상 · 콘텐츠 버튼 컬러(= 허브 카드 색) · 포인트 색.
    // 테마 자체는 저장하지 않는다(새 컬럼 없음) — 어느 테마인지는 지금 값이 프리셋과 같은지로 표시만 한다.
    // 고른 뒤 아래 색 칸에서 하나씩 손보면 '사용자 지정'이 된다.
    // 포인트 색 = 학습자 격자의 진행 막대·아이콘 상자·완료 체크(막대 바탕·아이콘 상자는 화면이 이 색을 옅게 섞어 만든다).
    // ⚠️ 포인트 색은 콘텐츠 버튼 CSS(JSON)의 accentColor 칸에 산다 — Builder.svelte ConversationButtonCss · 서버 buildEnterpriseCSSByDesign(--hub-point).
    const HUB_THEMES = [
        { key: "green", label: "초록 (시안)", accentColor: "#4eba1d", backColor: "#eaf5e1",
          block: { color: "#FFFFFF", lineColor: "#cfeabb", mouseOverColor: "#f3faee", mouseOverLineColor: "#4eba1d", fontColor: "#000000" } },
        { key: "blue", label: "파랑", accentColor: "#2f80ed", backColor: "#e7f0fb",
          block: { color: "#FFFFFF", lineColor: "#cfe0fa", mouseOverColor: "#f1f6fd", mouseOverLineColor: "#2f80ed", fontColor: "#000000" } },
        { key: "black", label: "검정", accentColor: "#222222", backColor: "#f2f2f2",
          block: { color: "#FFFFFF", lineColor: "#dddddd", mouseOverColor: "#f7f7f7", mouseOverLineColor: "#222222", fontColor: "#000000" } },
        { key: "orange", label: "주황", accentColor: "#f2994a", backColor: "#fdf1e6",
          block: { color: "#FFFFFF", lineColor: "#fbdcc0", mouseOverColor: "#fef7f0", mouseOverLineColor: "#f2994a", fontColor: "#000000" } },
        { key: "purple", label: "보라", accentColor: "#8b5cf6", backColor: "#f1ecfd",
          block: { color: "#FFFFFF", lineColor: "#ddd6fe", mouseOverColor: "#f7f4fe", mouseOverLineColor: "#8b5cf6", fontColor: "#000000" } },
        { key: "pink", label: "분홍", accentColor: "#ec4899", backColor: "#fdebf3",
          block: { color: "#FFFFFF", lineColor: "#fbcfe8", mouseOverColor: "#fef5f9", mouseOverLineColor: "#ec4899", fontColor: "#000000" } },
    ];
    const sameColor = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
    $: activeHubTheme = bot.isHub
        ? HUB_THEMES.find((t) => sameColor(bot.fixedConversationButtonCss?.accentColor, t.accentColor) && sameColor(bot.backColor, t.backColor))?.key ?? null
        : null;
    const applyHubTheme = (t) => bot.applyHubTheme({ backColor: t.backColor, block: t.block, accentColor: t.accentColor });
    const changeHubAccentColor = (e) => bot.changeHubAccentColor(e.target.color.toRgbString());

    // 반복되는 shadcn 유틸 조합 (PublishContext 의 inputCls 와 같은 패턴)
    const colorRowCls =
        "pd-color-row tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-border tw-bg-muted/30 tw-px-3 tw-py-2";
    const switchRowCls =
        "tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-lg tw-border tw-border-border tw-bg-muted/40 tw-px-4 tw-py-3";
</script>

<svelte:head>
    <script
        src="https://cdn.jsdelivr.net/npm/toolcool-color-picker/dist/toolcool-color-picker.min.js"
    ></script>
</svelte:head>

<div
    data-w-tab="Tab 3"
    class="design-tab-pane w-tab-pane tw-font-sans tw-text-foreground"
    class:w--tab-active={bot.currentTab === "pageDesign"}
>
    <div class="tw-space-y-4">
        <!-- 로고 -->
        <!-- ⚠️ 허브는 여기 없다 — [기본] 탭이 로고를 갖는다(이미지 자산에서 고른다).
             로고/캐릭터/소개 문구/멘트를 한 화면에 모으기 위해서다. 이 탭은 색·배경만 맡는다. -->
        {#if !bot.isEnterprise && !bot.isHub}
            <Section title="로고">
                <div class="tw-max-w-[260px]">
                    <ImageUploadCard
                        imageUrl={bot.logoImageContent
                            ? bot.getLogoImageContent()
                            : null}
                        hint="배경이 투명한 png 권장"
                        emptyText="클릭하거나 이미지를 끌어다 놓으세요"
                        minHeight="150px"
                        on:file={(e) => onLogoImageSelected(e.detail)}
                        on:remove={clickLogoImageDelete}
                    />
                </div>
            </Section>
        {/if}

        {#if bot.isHub}
            <!-- 허브 테마 색 — 배경·카드·포인트 색을 한 번에. 아래 칸들은 고른 뒤 하나씩 손볼 때 쓴다. -->
            <Section title="테마 색" description="고르면 배경 색상 · 카드(콘텐츠 버튼) 색 · 포인트 색이 한 번에 바뀝니다. 고른 뒤 아래에서 하나씩 손볼 수 있어요.">
                <div class="tw-grid tw-grid-cols-[repeat(auto-fill,minmax(120px,1fr))] tw-gap-3">
                    {#each HUB_THEMES as t (t.key)}
                        <button type="button"
                                class="tw-flex tw-flex-col tw-gap-2 tw-rounded-lg tw-border-2 tw-border-solid tw-p-2 tw-text-left tw-transition-colors {activeHubTheme === t.key ? 'tw-border-primary' : 'tw-border-transparent hover:tw-border-border'}"
                                aria-pressed={activeHubTheme === t.key}
                                on:click={() => applyHubTheme(t)}>
                            <!-- 미리보기: 테마 배경 위 카드 한 장(아이콘 상자 + 진행 막대) — 학습자 격자 카드 모양 그대로 -->
                            <span class="tw-block tw-rounded-md tw-p-2.5" style="background-color: {t.backColor}">
                                <span class="tw-block tw-space-y-1.5 tw-rounded-md tw-bg-white tw-p-2 tw-shadow-sm">
                                    <span class="tw-block tw-h-5 tw-w-5 tw-rounded" style="background-color: color-mix(in srgb, {t.accentColor} 30%, #fff)"></span>
                                    <span class="tw-block tw-h-1.5 tw-overflow-hidden tw-rounded-full" style="background-color: color-mix(in srgb, {t.accentColor} 15%, #fff)">
                                        <span class="tw-block tw-h-full tw-w-3/4 tw-rounded-full" style="background-color: {t.accentColor}"></span>
                                    </span>
                                </span>
                            </span>
                            <span class="tw-text-xs tw-font-medium tw-text-foreground">{t.label}</span>
                        </button>
                    {/each}
                </div>
                <div class="tw-mt-4 tw-max-w-md">
                    <div class={colorRowCls}>
                        <toolcool-color-picker class="color-target" color={bot.fixedConversationButtonCss?.accentColor || "#4eba1d"}
                            button-width="3.4rem" button-height="2rem" button-padding="2px" on:mouseup={changeHubAccentColor} />
                        <span class="tw-text-sm tw-text-foreground">
                            포인트 색 <span class="tw-text-xs tw-text-muted-foreground">— 진행 막대 · 아이콘 상자 · 완료 체크</span>
                        </span>
                    </div>
                </div>
            </Section>
        {/if}

        <!-- 배경 -->
        <Section title="배경">
            <div class="tw-grid tw-gap-5 md:tw-grid-cols-2">
                <!-- 배경 이미지 + 효과 토글 -->
                <div class="tw-flex tw-flex-col tw-gap-3">
                    {#if bot.isAvailableType("availableType", "BACK_IMAGE")}
                        <ImageUploadCard
                            imageUrl={bot.backImageContent
                                ? bot.getBackImageContent()
                                : null}
                            hint="채팅 화면 배경으로 쓰여요"
                            emptyText="클릭하거나 이미지를 끌어다 놓으세요"
                            minHeight="160px"
                            objectFit="cover"
                            on:file={(e) => onBackgroundImageSelected(e.detail)}
                            on:remove={clickBackImageDelete}
                        />
                    {:else}
                        <div
                            class="tw-flex tw-min-h-[160px] tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-border tw-border-dashed tw-border-border tw-bg-muted/40 tw-p-4 tw-text-center"
                        >
                            <img
                                src="/static/images/lock.svg"
                                alt=""
                                class="tw-h-6 tw-w-6 tw-opacity-70"
                            />
                            <span class="tw-text-sm tw-text-muted-foreground">
                                상위 요금제에서 배경 이미지를 쓸 수 있어요
                            </span>
                        </div>
                    {/if}

                    <div class={switchRowCls}>
                        <span class="tw-text-sm tw-text-foreground">배경 블러 효과</span>
                        <Switch
                            checked={bot.isBackBlurFlag()}
                            on:click={clickChangeBackBlurFlag}
                        />
                    </div>
                    <div class={switchRowCls}>
                        <span class="tw-text-sm tw-text-foreground">이미지 어둡게</span>
                        <Switch
                            checked={bot.isBackDarklyFlag()}
                            on:click={clickChangeBackDarklyFlag}
                        />
                    </div>
                </div>

                <!-- 배경 / 구분선 색상 -->
                <div class="tw-flex tw-flex-col tw-gap-2">
                    <div class={colorRowCls}>
                        <toolcool-color-picker
                            class="color-target"
                            color={bot.backColor}
                            button-width="3.4rem"
                            button-height="2rem"
                            button-padding="2px"
                            on:mouseup={changeBackColor}
                        />
                        <span class="tw-text-sm tw-text-foreground">배경 색상</span>
                    </div>
                    <div class={colorRowCls}>
                        <toolcool-color-picker
                            class="color-target"
                            color={bot.lineColor}
                            button-width="3.4rem"
                            button-height="2rem"
                            button-padding="2px"
                            on:mouseup={changeLineColor}
                        />
                        <span class="tw-text-sm tw-text-foreground">
                            구분선 / 아바톡로고 / 공유버튼 색상
                        </span>
                    </div>
                </div>
            </div>
        </Section>

        <!-- 버튼 모양 -->
        <Section title="버튼 모양">
            <div class="tw-space-y-5">
                <div>
                    <div class="tw-mb-2 tw-text-sm tw-font-medium tw-text-foreground">버튼 모서리</div>
                    <div class="tw-grid tw-grid-cols-3 tw-gap-3">
                        {#each roundOptions as o}
                            <button
                                type="button"
                                class="pd-tile"
                                class:pd-tile-on={bot.isButtonRoundChecked(o.round)}
                                on:click={() => selectRound(o.round)}
                            >
                                {#if !bot.isAvailableType("availableType", `BUTTON_ROUND_${o.round}`)}
                                    <img class="pd-tile-lock" src="/static/images/lock.svg" alt="" />
                                {/if}
                                <div
                                    class="pd-shape"
                                    style="border-radius:{o.radius}"
                                ></div>
                                <span class="pd-tile-label">{o.label}</span>
                            </button>
                        {/each}
                    </div>
                </div>

                <div>
                    <div class="tw-mb-2 tw-text-sm tw-font-medium tw-text-foreground">버튼 외곽선</div>
                    <div class="tw-grid tw-grid-cols-3 tw-gap-3">
                        {#each borderOptions as o}
                            <button
                                type="button"
                                class="pd-tile"
                                class:pd-tile-on={bot.isButtonBorderWidthChecked(o.width)}
                                on:click={() => selectBorderWidth(o.width)}
                            >
                                {#if o.width !== "0" && !bot.isAvailableType("availableType", `BUTTON_BORDER_${o.width}`)}
                                    <img class="pd-tile-lock" src="/static/images/lock.svg" alt="" />
                                {/if}
                                <div
                                    class="pd-shape pd-shape-border"
                                    style="border-width:{o.border}"
                                ></div>
                                <span class="pd-tile-label">{o.label}</span>
                            </button>
                        {/each}
                    </div>
                </div>

                <div class={switchRowCls}>
                    <span class="tw-text-sm tw-text-foreground">버튼 그림자</span>
                    <Switch
                        checked={bot.isButtonBoxShadowFlag()}
                        on:click={() => bot.changeButtonShadowFlag()}
                    />
                </div>
            </div>
        </Section>

        <!-- 버튼 컬러 -->
        <Section title="버튼 컬러">
            <!-- 안내 이미지·동작버튼은 2D 캐릭터형 전용이다. 셸 페이지(기업형·허브)엔 동작 버튼이 없다.
                 ⚠️ isEnterprise 가 아니라 isShellPage 로 볼 것 — 허브를 빼먹으면 2D 취급을 받는다. -->
            {#if !bot.isShellPage}
                <div class="tw-mb-4 tw-overflow-hidden tw-rounded-lg tw-border tw-border-border">
                    <img
                        src="/static/images/{lang}/a-button-info-image.png"
                        loading="lazy"
                        alt=""
                        class="tw-w-full"
                    />
                </div>
            {/if}

            <div
                class="tw-grid tw-gap-5 {!bot.isShellPage
                    ? 'lg:tw-grid-cols-3'
                    : ''}"
            >
                {#if !bot.isShellPage}
                    <!-- 동작버튼 컬러 (2D 전용) -->
                    <div class="tw-flex tw-flex-col tw-gap-2">
                        <div class="tw-text-sm tw-font-semibold tw-text-foreground">동작버튼 컬러</div>
                        <div class={colorRowCls}>
                            <toolcool-color-picker class="color-target" bind:this={refActionButtonColor} color={bot.actionButtonCss?.color} button-width="3.4rem" button-height="2rem" button-padding="2px" on:mouseup={changeActionButtonCss} />
                            <span class="tw-text-sm tw-text-foreground">버튼 색상</span>
                        </div>
                        <div class={colorRowCls}>
                            <toolcool-color-picker class="color-target" bind:this={refActionButtonLineColor} color={bot.actionButtonCss?.lineColor} button-width="3.4rem" button-height="2rem" button-padding="2px" on:mouseup={changeActionButtonCss} />
                            <span class="tw-text-sm tw-text-foreground">버튼선 색상</span>
                        </div>
                        <div class={colorRowCls}>
                            <toolcool-color-picker class="color-target" bind:this={refActionButtonMouseOverColor} color={bot.actionButtonCss?.mouseOverColor} button-width="3.4rem" button-height="2rem" button-padding="2px" on:mouseup={changeActionButtonCss} />
                            <span class="tw-text-sm tw-text-foreground">마우스 선택 시 버튼 색상</span>
                        </div>
                        <div class={colorRowCls}>
                            <toolcool-color-picker class="color-target" bind:this={refActionButtonMouseOverLineColor} color={bot.actionButtonCss?.mouseOverLineColor} button-width="3.4rem" button-height="2rem" button-padding="2px" on:mouseup={changeActionButtonCss} />
                            <span class="tw-text-sm tw-text-foreground">마우스 선택 시 버튼선 색상</span>
                        </div>
                        <div class={colorRowCls}>
                            <toolcool-color-picker class="color-target" bind:this={refActionButtonFontColor} color={bot.actionButtonCss?.fontColor} button-width="3.4rem" button-height="2rem" button-padding="2px" on:mouseup={changeActionButtonCss} />
                            <span class="tw-text-sm tw-text-foreground">버튼 폰트 색상</span>
                        </div>
                    </div>

                    <!-- 섹션 버튼 컬러 -->
                    <div class="tw-flex tw-flex-col tw-gap-2">
                        <div class="tw-text-sm tw-font-semibold tw-text-foreground">섹션 버튼 컬러</div>
                        <div class={colorRowCls}>
                            <toolcool-color-picker class="color-target" bind:this={refConversationButtonColor} color={bot.conversationButtonCss?.color} button-width="3.4rem" button-height="2rem" button-padding="2px" on:mouseup={changeConversationButtonCss} />
                            <span class="tw-text-sm tw-text-foreground">버튼 색상</span>
                        </div>
                        <div class={colorRowCls}>
                            <toolcool-color-picker class="color-target" bind:this={refConversationButtonLineColor} color={bot.conversationButtonCss?.lineColor} button-width="3.4rem" button-height="2rem" button-padding="2px" on:mouseup={changeConversationButtonCss} />
                            <span class="tw-text-sm tw-text-foreground">버튼선 색상</span>
                        </div>
                        <div class={colorRowCls}>
                            <toolcool-color-picker class="color-target" bind:this={refConversationButtonMouseOverColor} color={bot.conversationButtonCss?.mouseOverColor} button-width="3.4rem" button-height="2rem" button-padding="2px" on:mouseup={changeConversationButtonCss} />
                            <span class="tw-text-sm tw-text-foreground">마우스 선택 시 버튼 색상</span>
                        </div>
                        <div class={colorRowCls}>
                            <toolcool-color-picker class="color-target" bind:this={refConversationButtonMouseOverLineColor} color={bot.conversationButtonCss?.mouseOverLineColor} button-width="3.4rem" button-height="2rem" button-padding="2px" on:mouseup={changeConversationButtonCss} />
                            <span class="tw-text-sm tw-text-foreground">마우스 선택 시 버튼선 색상</span>
                        </div>
                        <div class={colorRowCls}>
                            <toolcool-color-picker class="color-target" bind:this={refConversationButtonFontColor} color={bot.conversationButtonCss?.fontColor} button-width="3.4rem" button-height="2rem" button-padding="2px" on:mouseup={changeConversationButtonCss} />
                            <span class="tw-text-sm tw-text-foreground">버튼 폰트 색상</span>
                        </div>
                    </div>
                {/if}

                <!-- 콘텐츠 버튼 컬러 (엔터프라이즈 포함 항상 노출) -->
                <div class="tw-flex tw-flex-col tw-gap-2">
                    {#if !bot.isShellPage}
                        <div class="tw-text-sm tw-font-semibold tw-text-foreground">콘텐츠 버튼 컬러</div>
                    {/if}
                    <!-- 셸 페이지(기업형·허브)에선 이 그룹만 나오므로 세로로 길어지지 않게 여러 열로 편다.
                         ⚠️ 허브에서도 이 색은 살아 있다 — 카드가 --enterprise-block-* 변수를 쓴다(hub.css). -->
                    <div
                        class={bot.isShellPage
                            ? "tw-grid tw-gap-2 sm:tw-grid-cols-2 lg:tw-grid-cols-3"
                            : "tw-flex tw-flex-col tw-gap-2"}
                    >
                        <div class={colorRowCls}>
                            <toolcool-color-picker class="color-target" bind:this={refFixedConversationButtonColor} color={bot.fixedConversationButtonCss?.color} button-width="3.4rem" button-height="2rem" button-padding="2px" on:mouseup={changeFixedConversationButtonCss} />
                            <span class="tw-text-sm tw-text-foreground">버튼 색상</span>
                        </div>
                        <div class={colorRowCls}>
                            <toolcool-color-picker class="color-target" bind:this={refFixedConversationButtonLineColor} color={bot.fixedConversationButtonCss?.lineColor} button-width="3.4rem" button-height="2rem" button-padding="2px" on:mouseup={changeFixedConversationButtonCss} />
                            <span class="tw-text-sm tw-text-foreground">버튼선 색상</span>
                        </div>
                        <div class={colorRowCls}>
                            <toolcool-color-picker class="color-target" bind:this={refFixedConversationButtonMouseOverColor} color={bot.fixedConversationButtonCss?.mouseOverColor} button-width="3.4rem" button-height="2rem" button-padding="2px" on:mouseup={changeFixedConversationButtonCss} />
                            <span class="tw-text-sm tw-text-foreground">마우스 선택 시 버튼 색상</span>
                        </div>
                        <div class={colorRowCls}>
                            <toolcool-color-picker class="color-target" bind:this={refFixedConversationButtonMouseOverLineColor} color={bot.fixedConversationButtonCss?.mouseOverLineColor} button-width="3.4rem" button-height="2rem" button-padding="2px" on:mouseup={changeFixedConversationButtonCss} />
                            <span class="tw-text-sm tw-text-foreground">마우스 선택 시 버튼선 색상</span>
                        </div>
                        <div class={colorRowCls}>
                            <toolcool-color-picker class="color-target" bind:this={refFixedConversationButtonFontColor} color={bot.fixedConversationButtonCss?.fontColor} button-width="3.4rem" button-height="2rem" button-padding="2px" on:mouseup={changeFixedConversationButtonCss} />
                            <span class="tw-text-sm tw-text-foreground">버튼 폰트 색상</span>
                        </div>
                    </div>
                </div>
            </div>
        </Section>

        <!-- 폰트 스타일 -->
        <Section title="폰트 스타일">
            <div class="tw-space-y-5">
                <div class="tw-grid tw-gap-2 sm:tw-grid-cols-2">
                    <div class={colorRowCls}>
                        <toolcool-color-picker
                            class="color-target"
                            color={bot.conversationTextColor}
                            button-width="3.4rem"
                            button-height="2rem"
                            button-padding="2px"
                            on:mouseup={changeConversationTextColor}
                        />
                        <span class="tw-text-sm tw-text-foreground">텍스트 색상</span>
                    </div>
                    <div class={colorRowCls}>
                        <toolcool-color-picker
                            class="color-target"
                            color={bot.conversationLineColor}
                            button-width="3.4rem"
                            button-height="2rem"
                            button-padding="2px"
                            on:mouseup={changeConversationLineColor}
                        />
                        <span class="tw-text-sm tw-text-foreground">텍스트 라인 색상</span>
                    </div>
                </div>

                <div class="tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-4">
                    {#each fontList as f}
                        <button
                            type="button"
                            class="pd-tile"
                            class:pd-tile-on={bot.isConversationTextTypeChecked(f.key)}
                            on:click={() => selectFont(f.key)}
                        >
                            {#if !bot.isAvailableType("fontType", f.key)}
                                <img class="pd-tile-lock" src="/static/images/lock.svg" alt="" />
                            {/if}
                            <span class="pd-font-sample" style="font-family:'{f.key}'">{f.sample}</span>
                            <span class="pd-tile-label">{f.label}</span>
                        </button>
                    {/each}
                </div>
            </div>
        </Section>

        <!-- 추가 설정 -->
        <Section title="추가 설정">
            <svelte:fragment slot="badge">
                {#if !bot.isAvailableType("availableType", "SHARE_BUTTON")}
                    <img class="tw-h-4 tw-w-4 tw-opacity-70" src="/static/images/lock.svg" alt="" />
                {/if}
            </svelte:fragment>
            <div class="tw-space-y-2">
                <div class={switchRowCls}>
                    <span class="tw-text-sm tw-text-foreground">공유버튼 추가</span>
                    <Switch
                        checked={bot.isShareButtonFlag()}
                        on:click={clickChangeShareView}
                    />
                </div>
                <div class={switchRowCls}>
                    <span class="tw-text-sm tw-text-foreground">하단 아바톡 로고 지우기</span>
                    <Switch
                        checked={bot.isAvatalkImageFlag()}
                        on:click={clickChangeLogoView}
                    />
                </div>
            </div>
        </Section>
    </div>
</div>

<style>
    /* ── 선택형 타일 (버튼 모양 / 외곽선 / 폰트) ── shadcn 카드 + primary 선택 링 ── */
    .pd-tile {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 12px;
        border: 2px solid hsl(var(--sb-border));
        border-radius: 12px;
        background: hsl(var(--sb-card));
        cursor: pointer;
        transition: border-color 0.15s, box-shadow 0.15s;
    }
    .pd-tile:hover {
        border-color: hsl(var(--sb-primary) / 0.5);
    }
    .pd-tile-on {
        border-color: hsl(var(--sb-primary));
        box-shadow: 0 0 0 1px hsl(var(--sb-primary)) inset;
    }
    .pd-tile-label {
        font-size: 13px;
        color: hsl(var(--sb-muted-foreground));
        white-space: nowrap;
    }
    .pd-tile-on .pd-tile-label {
        color: hsl(var(--sb-primary));
        font-weight: 600;
    }
    .pd-tile-lock {
        position: absolute;
        top: 8px;
        right: 8px;
        width: 16px;
        height: 16px;
    }

    /* 모양 미리보기 박스 */
    .pd-shape {
        width: 100%;
        height: 40px;
        background: hsl(var(--sb-muted) / 0.6);
        border: 1px solid hsl(var(--sb-border));
    }
    .pd-shape-border {
        border-radius: 8px;
        border-style: solid;
        border-color: hsl(var(--sb-foreground) / 0.55);
    }

    /* 폰트 미리보기 */
    .pd-font-sample {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 34px;
        font-size: 18px;
        color: hsl(var(--sb-foreground));
        line-height: 1;
    }

    /* ── 색상 피커 행: 팝업이 아래 행에 가리지 않도록 스택 순서 관리 (ChatBubbleColorPicker 규칙 이식) ── */
    .pd-color-row {
        position: relative;
        z-index: 10;
    }
    .pd-color-row:focus-within {
        z-index: 60;
    }
    .pd-color-row :global(toolcool-color-picker) {
        position: relative;
        z-index: 40;
    }
</style>
